import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPin } from "lucide-react";
import { loadMaps, geocodeAddress } from "@/lib/geo";
import api from "@/lib/api";

const haversineKm = (a, b) => {
  const R = 6371, toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};
const dot = (g, color) => ({ path: g.maps.SymbolPath.CIRCLE, scale: 7, fillColor: color, fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 3 });

// Snap a raw GPS point onto the nearest point of the route polyline so the marker sits on the road.
const snapToPath = (p, path) => {
  if (!path || path.length < 2) return p;
  const kx = Math.cos((p.lat * Math.PI) / 180);
  const px = p.lng * kx, py = p.lat;
  let best = p, bestD = Infinity;
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i], b = path[i + 1];
    const ax = a.lng * kx, ay = a.lat, bx = b.lng * kx, by = b.lat;
    const dx = bx - ax, dy = by - ay;
    const t = ((px - ax) * dx + (py - ay) * dy) / ((dx * dx + dy * dy) || 1e-12);
    const tc = Math.max(0, Math.min(1, t));
    const cx = ax + tc * dx, cy = ay + tc * dy;
    const dd = (px - cx) ** 2 + (py - cy) ** 2;
    if (dd < bestD) { bestD = dd; best = { lat: cy, lng: cx / kx }; }
  }
  return best;
};
// Glide the marker to its new position so it visibly travels along the road between updates.
const animateMarker = (marker, to, animRef) => {
  const cur = marker.getPosition && marker.getPosition();
  if (!cur) { marker.setPosition(to); return; }
  const from = { lat: cur.lat(), lng: cur.lng() };
  const start = performance.now(), dur = 3500;
  if (animRef.current) cancelAnimationFrame(animRef.current);
  const step = (now) => {
    const t = Math.min(1, (now - start) / dur);
    marker.setPosition({ lat: from.lat + (to.lat - from.lat) * t, lng: from.lng + (to.lng - from.lng) * t });
    if (t < 1) animRef.current = requestAnimationFrame(step);
  };
  animRef.current = requestAnimationFrame(step);
};

export const LiveMap = ({ bookingId, shareToken, destText }) => {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const driverRef = useRef(null);
  const riderRef = useRef(null);
  const riderLoc = useRef(null);
  const destLoc = useRef(null);
  const lineRef = useRef(null);
  const rendererRef = useRef(null);
  const dirRef = useRef(null);
  const geocoderRef = useRef(null);
  const lastGeoRef = useRef(0);
  const lastRouteRef = useRef(0);
  const rideStatusRef = useRef(null);
  const routePathRef = useRef(null);
  const animRef = useRef(null);
  const routeKmRef = useRef(0);
  const [eta, setEta] = useState(null);
  const [addr, setAddr] = useState("");
  const [status, setStatus] = useState("loading");
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    if (!apiKey || !elRef.current) { setStatus("error"); return; }
    let cancelled = false;
    loadMaps(apiKey).then(() => {
      if (cancelled || !elRef.current) return;
      const g = window.google;
      mapRef.current = new g.maps.Map(elRef.current, { center: { lat: 20.5937, lng: 78.9629 }, zoom: 5, disableDefaultUI: true, zoomControl: true, clickableIcons: false, gestureHandling: "greedy" });
      setStatus("ok");
      if (!shareToken && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => { const p = { lat: pos.coords.latitude, lng: pos.coords.longitude }; riderLoc.current = p; if (mapRef.current) riderRef.current = new g.maps.Marker({ position: p, map: mapRef.current, icon: dot(g, "#059669") }); },
          () => {}, { enableHighAccuracy: true, timeout: 8000 }
        );
      }
      if (destText) {
        geocodeAddress(destText).then((loc) => { if (loc && mapRef.current) { destLoc.current = loc; new window.google.maps.Marker({ position: loc, map: mapRef.current, icon: dot(window.google, "#059669") }); } });
      }
    }).catch(() => { if (!cancelled) setStatus("error"); });
    return () => { cancelled = true; };
  }, [apiKey, shareToken, destText]);

  useEffect(() => {
    if (status !== "ok") return;
    let alive = true;
    let timer = null;
    const poll = async () => {
      if (document.hidden) return; // usage guard: don't burn API calls on a hidden tab
      try {
        const r = shareToken
          ? await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/ride-track/${shareToken}`)
          : await api.get(`/services/booking/${bookingId}/driver-location`);
        const loc = r.data?.location;
        if (r.data?.ride_status) rideStatusRef.current = r.data.ride_status;
        const g = window.google;
        const map = mapRef.current;
        if (alive && loc && map && g) {
          const rawD = { lat: loc.lat, lng: loc.lng };
          // Keep the marker on the road by snapping to the last computed route polyline.
          const d = routePathRef.current ? snapToPath(rawD, routePathRef.current) : rawD;
          if (!driverRef.current) driverRef.current = new g.maps.Marker({ position: d, map, icon: dot(g, "#0284c7") });
          else animateMarker(driverRef.current, d, animRef);
          // During pickup the driver heads to the rider; once the ride is in progress, route to the destination.
          const inProgress = rideStatusRef.current === "in_progress";
          const target = inProgress ? (destLoc.current || riderLoc.current) : (riderLoc.current || destLoc.current);
          if (target) {
            const nowT = Date.now();
            if (nowT - lastRouteRef.current > 20000) {
              lastRouteRef.current = nowT;
              if (!dirRef.current) dirRef.current = new g.maps.DirectionsService();
              dirRef.current.route({ origin: d, destination: target, travelMode: g.maps.TravelMode.DRIVING,
                drivingOptions: { departureTime: new Date(), trafficModel: g.maps.TrafficModel.BEST_GUESS } }, (res, st) => {
                if (st === "OK" && res) {
                  if (lineRef.current) { lineRef.current.setMap(null); lineRef.current = null; }
                  if (!rendererRef.current) rendererRef.current = new g.maps.DirectionsRenderer({ map, suppressMarkers: true, preserveViewport: true, polylineOptions: { strokeColor: "#0284c7", strokeWeight: 5, strokeOpacity: 0.9 } });
                  rendererRef.current.setDirections(res);
                  routePathRef.current = (res.routes?.[0]?.overview_path || []).map((x) => ({ lat: x.lat(), lng: x.lng() }));
                  const leg = res.routes?.[0]?.legs?.[0];
                  if (leg) {
                    const secs = leg.duration_in_traffic?.value || leg.duration?.value || 0;
                    setEta({ km: leg.distance?.text || "", mins: Math.max(1, Math.round(secs / 60)), road: true, traffic: !!leg.duration_in_traffic });
                    // Push Google route distance to the booking — single source of distance for per-KM fare.
                    if (!shareToken && bookingId && inProgress !== true) {
                      const km = (leg.distance?.value || 0) / 1000;
                      if (km > 0 && Math.abs(km - (routeKmRef.current || 0)) > 0.05) {
                        routeKmRef.current = km;
                        api.post(`/rides/${bookingId}/route-distance`, { km }).catch(() => {});
                      }
                    }
                  }
                } else {
                  routePathRef.current = null;
                  if (!lineRef.current) lineRef.current = new g.maps.Polyline({ path: [d, target], map, geodesic: true, strokeColor: "#0284c7", strokeOpacity: 0.9, strokeWeight: 4 });
                  else lineRef.current.setPath([d, target]);
                  const km = haversineKm(d, target);
                  setEta({ km: km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`, mins: Math.max(1, Math.round((km / 22) * 60)), road: false });
                }
              });
            } else if (lineRef.current) {
              lineRef.current.setPath([d, target]);
            }
            const b = new g.maps.LatLngBounds(); b.extend(d); b.extend(target); map.fitBounds(b, 60);
          } else {
            map.panTo(d);
            if ((map.getZoom() || 5) < 13) map.setZoom(15);
          }
          const ng = Date.now();
          if (ng - lastGeoRef.current > 15000) {
            lastGeoRef.current = ng;
            if (!geocoderRef.current) geocoderRef.current = new g.maps.Geocoder();
            geocoderRef.current.geocode({ location: d }, (rr, ss) => { if (ss === "OK" && rr && rr[0]) setAddr(rr[0].formatted_address); });
          }
        }
        if (r.data?.ride_status && !["assigned", "in_progress"].includes(r.data.ride_status) && timer) clearInterval(timer);
      } catch { /* ignore transient poll errors */ }
    };
    poll();
    timer = setInterval(poll, 10000); // smart update frequency — every 10s keeps us well inside the free tier
    const onVis = () => { if (!document.hidden) poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; if (timer) clearInterval(timer); if (animRef.current) cancelAnimationFrame(animRef.current); document.removeEventListener("visibilitychange", onVis); };
  }, [status, bookingId, shareToken]);

  if (!apiKey) {
    return <div className="flex h-[210px] items-center justify-center rounded-xl bg-slate-100 text-xs text-slate-400" data-testid="live-driver-map">Map unavailable</div>;
  }
  return (
    <div>
      {eta && status === "ok" && (
        <div className="mb-1 inline-flex items-center gap-1.5 rounded-full bg-sky-600 px-2.5 py-1 text-[11px] font-bold text-white" data-testid="cb-eta">
          {eta.road ? (eta.traffic ? "Live traffic route" : "Route") : "Driver"} ~{eta.km} away · ~{eta.mins} min
        </div>
      )}
      <div ref={elRef} data-testid="live-driver-map" style={{ height: 210, width: "100%", borderRadius: 12, overflow: "hidden", background: "#e5e3df" }} />
      {addr && status === "ok" && (
        <p className="mt-1 flex items-start gap-1 text-[11px] text-slate-600" data-testid="cb-driver-addr">
          <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-sky-600" /> Driver near {addr}
        </p>
      )}
      {status === "error" && <p className="mt-1 text-[10px] text-slate-400">Live map couldn't load.</p>}
    </div>
  );
};

export default LiveMap;
